import { useCallback, useEffect, useState } from 'react';
import type { SortingState } from '@tanstack/react-table';
import { Toaster } from '@/components/ui/toaster';
import { DataTableFooter } from '@components/DataTable/DataTableFooter';
import { Page } from '../layouts/Page';
import { PageHeaderSearchBar, PageHeaderTotalLabel } from '../layouts/PageHeader';
import { ResponsivePageHeader } from '../layouts/ResponsivePageHeader';
import { openMobileSidebar } from '../MobileSidebar/mobileSidebarEvents';
import { AllOrderSourcesPage } from '../OrderSource/Page/AllOrderSourcesPage';
import { CreateOrderSourceDialog } from '../OrderSource/Sections/CreateOrderSourceDialog';
import { OrderSourcePreviewDrawer } from '../OrderSource/Sections/OrderSourcePreviewDrawer';
import { ConfirmationDialog } from '../common/ConfirmationDialog';
import type { OrderSource } from '../OrderSource/types';
import { SEARCH_BAR_WIDTH } from '../../constants';
import {
  useOrderSources,
  useOrderSourceRouterSearch,
  useUpdateOrderSourceStatus,
} from '@hooks/orderSources';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { searchQueryParam } from '@/react/utils/searchQuery';
import { useTranslation } from '../../hooks/useTranslation';
import type { OrderSourceTranslations } from '../OrderSource/types';
import { can, CAPABILITIES, type CurrentUser } from '../../lib/capabilities';

interface OrderSourceManagementProps {
  translations?: OrderSourceTranslations;
  currentUser?: CurrentUser;
}

const DEFAULT_TRANSLATIONS: OrderSourceManagementProps['translations'] = {};
const DEBOUNCE_DELAY = 300;

export default function OrderSourceManagement({
  translations = DEFAULT_TRANSLATIONS,
  currentUser,
}: OrderSourceManagementProps) {
  const { t } = useTranslation();
  // RBAC: gate New / Sync / Edit / status-toggle on order_sources.write. The
  // server policy stays the source of truth; this only hides the affordances.
  const canWrite = can(currentUser, CAPABILITIES.ORDER_SOURCES_WRITE);
  const router = useOrderSourceRouterSearch();
  const { search } = router;

  const [searchQuery, setSearchQuery] = useState(search.q);
  const debouncedSearch = useDebouncedValue(searchQuery, DEBOUNCE_DELAY);

  // Re-sync local input when URL changes externally (back/forward navigation).
  useEffect(() => {
    setSearchQuery(search.q);
  }, [search.q]);

  // Sync debounced typing back to URL.
  useEffect(() => {
    if (debouncedSearch !== search.q) {
      router.setQuery(debouncedSearch);
    }
  }, [debouncedSearch, search.q, router]);

  const sorting: SortingState = search.sort
    ? [{ id: search.sort, desc: search.direction === 'desc' }]
    : [];

  const handleSortingChange = useCallback(
    (newSorting: SortingState) => {
      if (newSorting.length > 0) {
        router.setSort(newSorting[0].id, newSorting[0].desc ? 'desc' : 'asc');
      } else {
        router.navigate({ sort: '', direction: '', page: 1 });
      }
    },
    [router],
  );

  // Always send explicit sort params. Listable's default direction is "desc"
  // but our default UI sort is name ASC, so omitting these would flip the order.
  const apiParams = {
    status: search.status,
    q: searchQueryParam(search.q),
    page: search.page,
    perPage: search.perPage,
    sortBy: search.sort || 'name',
    sortDirection: (search.direction || 'asc') as 'asc' | 'desc',
  };

  const { data, isLoading, isFetching } = useOrderSources({ params: apiParams });

  const orderSources = data?.order_sources ?? [];
  const totalCount = data?.pagination?.total ?? 0;
  const totalUnfiltered = data?.total_unfiltered ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / search.perPage));

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState<OrderSource | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Single mutation + dialog instance for the whole table (rather than one per
  // row inside ActionsCell). Mirrors ResaleItemManagement.
  const updateStatus = useUpdateOrderSourceStatus();
  const [confirmDeactivateId, setConfirmDeactivateId] = useState<number | null>(null);

  const handleStatusToggle = useCallback(
    (id: number, active: boolean) => {
      // Deactivate prompts for confirmation; activate fires immediately.
      if (active) {
        if (updateStatus.isPending) return;
        updateStatus.mutate({ id, active: true });
      } else {
        setConfirmDeactivateId(id);
      }
    },
    [updateStatus],
  );

  const handleConfirmDeactivate = useCallback(() => {
    if (confirmDeactivateId == null) return;
    updateStatus.mutate({ id: confirmDeactivateId, active: false });
    setConfirmDeactivateId(null);
  }, [confirmDeactivateId, updateStatus]);

  const handleCreateNew = useCallback(() => {
    setCreateDialogOpen(true);
  }, []);

  const handleRowClick = useCallback((orderSource: OrderSource) => {
    setPreviewItem(orderSource);
    setPreviewOpen(true);
  }, []);

  const handlePreviewOpenChange = useCallback((open: boolean) => {
    setPreviewOpen(open);
    if (!open) {
      // Defer clearing the item so the drawer body stays populated during the
      // close animation (OrderSourcePreviewDrawer's stable-id hook handles this).
      setTimeout(() => setPreviewItem(null), 200);
    }
  }, []);

  return (
    <>
      <Toaster />
      <Page>
        <Page.Header className="py-6 md:py-0">
          <ResponsivePageHeader
            mobile={{
              title: t('header.title'),
              count: isLoading ? '…' : totalCount.toLocaleString(),
              searchValue: searchQuery,
              onSearchChange: setSearchQuery,
              searchPlaceholder: t('search.placeholder'),
              searchAriaLabel: t('search.aria_label'),
              openNavigationAriaLabel: t('header.open_navigation'),
              openSearchAriaLabel: t('header.open_search'),
              closeSearchAriaLabel: t('header.close_search'),
              onOpenNavigation: openMobileSidebar,
            }}
          >
            <div className="shrink-0">
              <PageHeaderTotalLabel
                count={totalUnfiltered}
                label={t('header.total_unfiltered')}
              />
            </div>
            <div
              className="flex-1 min-w-0 flex justify-center"
              style={{ flexBasis: SEARCH_BAR_WIDTH }}
            >
              <PageHeaderSearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={t('search.placeholder')}
                ariaLabel={t('search.aria_label')}
              />
            </div>
            <div
              className="min-w-0 invisible"
              aria-hidden="true"
              style={{ flexShrink: 99 }}
            >
              <PageHeaderTotalLabel
                count={totalUnfiltered}
                label={t('header.total_unfiltered')}
              />
            </div>
          </ResponsivePageHeader>
        </Page.Header>
        <Page.Body>
          <AllOrderSourcesPage
            currentStatus={search.status}
            totalCount={totalCount}
            onStatusChange={router.setStatus}
            translations={translations}
            isLoading={isLoading}
            isFetching={isFetching}
            orderSources={orderSources}
            currentPage={search.page}
            pageSize={search.perPage}
            onPageChange={router.setPage}
            onPerPageChange={router.setPerPage}
            onCreateNew={handleCreateNew}
            onRowClick={handleRowClick}
            onStatusToggle={handleStatusToggle}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            canWrite={canWrite}
          />
        </Page.Body>
        <Page.Footer>
          <DataTableFooter
            currentPage={Math.min(search.page, totalPages)}
            totalPages={totalPages}
            totalCount={totalCount}
            perPage={search.perPage}
            onPageChange={router.setPage}
            onPerPageChange={router.setPerPage}
            className="flex flex-col justify-center h-full px-6 sm:px-8 lg:px-16"
          />
        </Page.Footer>
      </Page>

      <CreateOrderSourceDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      <OrderSourcePreviewDrawer
        orderSource={previewItem}
        open={previewOpen}
        onOpenChange={handlePreviewOpenChange}
        canWrite={canWrite}
      />

      <ConfirmationDialog
        open={confirmDeactivateId != null}
        onOpenChange={(open) => {
          if (!open) setConfirmDeactivateId(null);
        }}
        onConfirm={handleConfirmDeactivate}
        title={t('status_dropdown.deactivate_confirm_title')}
        message={t('status_dropdown.deactivate_confirm_message')}
        messageClassName="max-w-none"
        confirmLabel={t('status_dropdown.deactivate_confirm_action')}
        cancelLabel={t('status_dropdown.deactivate_cancel_action')}
        variant="destructive"
      />
    </>
  );
}
